/**
 * cart-abo-start.js
 *
 * Füllt das Dropdown "Abo beginnen ab:" mit den Ausgaben des jeweiligen
 * Produkts – produktspezifisch, da jedes Magazin einen anderen
 * Erscheinungs-Zyklus hat (custom.abo_ausgaben_pro_jahr).
 *
 * Zwei Anzeige-Varianten:
 * 1. Standard: "Ausgabe X/YY" – rein aus dem heutigen Datum berechnet
 *    (kein Ankerpunkt gesetzt).
 * 2. Mit zusätzlicher fortlaufender Heftnummer (z.B. SCHWARZWEISS):
 *    "Ausgabe X/YY (Heft N)" – sobald ein Ankerpunkt gesetzt ist
 *    (custom.abo_heft_referenz_nummer + custom.abo_heft_referenz_datum).
 *
 * WICHTIG: Ist ein Ankerpunkt gesetzt, wird die "Ausgabe X/YY"-Zahl
 * NICHT mehr unabhängig aus dem heutigen Kalendermonat berechnet,
 * sondern vom selben Ankerpunkt und derselben Versatz-Rechnung wie die
 * Heftnummer abgeleitet. Sonst könnten beide Zahlen auseinanderlaufen,
 * wenn der reale Erscheinungsrhythmus nicht exakt auf das
 * Kalenderjahr-Raster (Jan/Feb, Mär/Apr, ...) fällt.
 *
 * Auswahl ist PFLICHT: "Zur Kasse" bleibt gesperrt, bis für jede
 * betroffene Warenkorb-Position eine konkrete Ausgabe gewählt wurde.
 * Läuft über dieselbe zentrale Blocker-Registry wie Prämie und
 * Studentennachweis (cart-checkout-guard.js).
 */

(function () {
  /**
   * Berechnet Ausgabennummer und Jahr für einen Versatz relativ zu
   * einem beliebigen Referenzpunkt (nicht zwingend das heutige Datum).
   */
  function ausgabeFuerOffset(referenzAusgabenNummer, referenzJahr, ausgabenProJahr, offset) {
    const nullBasiert = (referenzAusgabenNummer - 1) + offset;
    const jahrVersatz = Math.floor(nullBasiert / ausgabenProJahr);
    const ausgabenIndex = ((nullBasiert % ausgabenProJahr) + ausgabenProJahr) % ausgabenProJahr;

    return {
      nummer: ausgabenIndex + 1,
      jahr: referenzJahr + jahrVersatz,
    };
  }

  function jahrgangWert(nummer, jahr) {
    const jahrKurz = String(jahr).slice(-2);
    return 'Ausgabe ' + nummer + '/' + jahrKurz;
  }

  /**
   * Ermittelt den gemeinsamen Rechen-Ausgangspunkt: entweder den
   * gesetzten Heft-Ankerpunkt (Referenznummer + Referenzdatum), oder
   * ersatzweise das heutige Datum, wenn kein Ankerpunkt vorliegt.
   * Liefert außerdem den Versatz (in Ausgaben) vom Ausgangspunkt bis
   * zur heute laufenden Ausgabe.
   */
  function ausgangspunktErmitteln(ausgabenProJahr, referenzHeftnummer, referenzDatumString) {
    const monateProAusgabe = 12 / ausgabenProJahr;
    const heute = new Date();

    if (referenzHeftnummer !== null && referenzDatumString) {
      const referenzDatum = new Date(referenzDatumString + 'T00:00:00');

      // Ausgabennummer-im-Jahr und Jahr für den Referenzpunkt selbst –
      // nach demselben Kalenderraster wie sonst auch, aber bezogen auf
      // das Referenzdatum statt auf heute.
      const referenzMonat = referenzDatum.getMonth() + 1;
      const referenzAusgabenNummerImJahr = Math.ceil(referenzMonat / monateProAusgabe);
      const referenzJahr = referenzDatum.getFullYear();

      // Versatz vom Referenzpunkt bis heute, in ganzen Ausgaben
      const monateVergangen =
        (heute.getFullYear() - referenzDatum.getFullYear()) * 12 +
        (heute.getMonth() - referenzDatum.getMonth());
      const versatzBisHeute = Math.floor(monateVergangen / monateProAusgabe);

      return {
        ausgabenNummerImJahr: referenzAusgabenNummerImJahr,
        jahr: referenzJahr,
        versatzBisHeute: versatzBisHeute,
        heftnummerBasis: referenzHeftnummer,
      };
    }

    // Kein Ankerpunkt: heutiges Datum ist der Ausgangspunkt, kein Versatz nötig
    const aktuellerMonat = heute.getMonth() + 1;
    return {
      ausgabenNummerImJahr: Math.ceil(aktuellerMonat / monateProAusgabe),
      jahr: heute.getFullYear(),
      versatzBisHeute: 0,
      heftnummerBasis: null,
    };
  }

  /**
   * Erzeugt die vollständige Ausgabenliste. Wenn ein Heft-Ankerpunkt
   * vorliegt, wird die Heftnummer zusätzlich zur Jahrgangs-Ausgabe
   * angezeigt – beide Zahlen laufen dann vom selben Ausgangspunkt.
   */
  function ausgabenlisteErzeugen(ausgabenProJahr, referenzHeftnummer, referenzDatumString) {
    const ausgangspunkt = ausgangspunktErmitteln(ausgabenProJahr, referenzHeftnummer, referenzDatumString);

    function eintragErzeugen(listOffset) {
      const gesamtVersatz = ausgangspunkt.versatzBisHeute + listOffset;
      const { nummer, jahr } = ausgabeFuerOffset(
        ausgangspunkt.ausgabenNummerImJahr,
        ausgangspunkt.jahr,
        ausgabenProJahr,
        gesamtVersatz
      );
      let wert = jahrgangWert(nummer, jahr);
      if (ausgangspunkt.heftnummerBasis !== null) {
        wert += ' (Heft ' + (ausgangspunkt.heftnummerBasis + gesamtVersatz) + ')';
      }
      return { label: wert, value: wert };
    }

    const ausgaben = [];
    [-2, -1, 0].forEach(function (offset) {
      ausgaben.push(eintragErzeugen(offset));
    });
    for (let i = 1; i <= ausgabenProJahr; i++) {
      ausgaben.push(eintragErzeugen(i));
    }

    return ausgaben;
  }

  function dropdownBefuellen(select, container, vorausgewaehlterWert) {
    // Verhindert doppeltes Befüllen falls init() mehrfach für dasselbe
    // (bereits befüllte) Select läuft.
    if (select.dataset.aboStartBefuellt === 'true') return;

    const ausgabenProJahr = parseInt(container.getAttribute('data-ausgaben-pro-jahr'), 10);
    if (!ausgabenProJahr || ausgabenProJahr < 1) return;

    const referenzNummerAttr = container.getAttribute('data-heft-referenz-nummer');
    const referenzDatumAttr = container.getAttribute('data-heft-referenz-datum');
    const referenzHeftnummer = referenzNummerAttr ? parseInt(referenzNummerAttr, 10) : null;

    const ausgaben = ausgabenlisteErzeugen(ausgabenProJahr, referenzHeftnummer, referenzDatumAttr);
    ausgaben.forEach(function (ausgabe) {
      const option = document.createElement('option');
      option.value = ausgabe.value;
      option.textContent = ausgabe.label;
      if (ausgabe.value === vorausgewaehlterWert) {
        option.selected = true;
      }
      select.appendChild(option);
    });

    select.dataset.aboStartBefuellt = 'true';
  }

  function alleContainer() {
    return document.querySelectorAll('.cart-item__abo-start');
  }

  function statusTextSetzen(container, text) {
    const status = container.querySelector('.cart-item__abo-start-status');
    if (status) status.textContent = text;
  }

  function gesamtstatusPruefen() {
    let irgendwasOffen = false;

    alleContainer().forEach(function (container) {
      const select = container.querySelector('.cart-item__abo-start-select');
      if (!select || select.value === '') {
        irgendwasOffen = true;
        statusTextSetzen(container, 'Bitte eine Ausgabe auswählen.');
      } else {
        statusTextSetzen(container, '');
      }
    });

    if (window.cartCheckoutGuard) {
      window.cartCheckoutGuard.setBlocker(
        'abostart',
        irgendwasOffen,
        'Bitte wählen Sie für jedes Abo eine Startausgabe aus.'
      );
    }
  }

  function init() {
    alleContainer().forEach(function (container) {
      const select = container.querySelector('.cart-item__abo-start-select');
      if (!select) return;

      const vorausgewaehltEl = container.querySelector('.cart-item__abo-start-vorausgewaehlt');
      const vorausgewaehlterWert = vorausgewaehltEl?.getAttribute('data-vorausgewaehlt') || '';

      dropdownBefuellen(select, container, vorausgewaehlterWert);

      // Verhindert doppelt gebundene change-Listener bei mehrfachem init()
      if (select.dataset.aboStartListenerGebunden === 'true') return;
      select.dataset.aboStartListenerGebunden = 'true';

      select.addEventListener('change', function (e) {
        e.stopPropagation();
        gesamtstatusPruefen();
        const zeile = container.closest('tr.cart-item');
        if (zeile && window.cartLineSync) {
          window.cartLineSync.aktualisieren(zeile);
        }
      });
    });

    gesamtstatusPruefen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Das Theme ersetzt bei Mengenänderungen (+/- bzw. Entfernen) den
  // Warenkorb-Bereich per Ajax und meldet das danach über 'cart-update'.
  // Die frisch gerenderten Elemente brauchen dann erneut ihre Listener
  // bzw. Dropdown-Optionen. Event-basiert statt MutationObserver, damit
  // keine Endlosschleife durch eigene DOM-Änderungen entstehen kann.
  if (typeof subscribe === 'function') {
    subscribe('cart-update', function () {
      init();
    });
  }
})();
